import { Controller } from "@hotwired/stimulus";

// Constrained rich-text field (SCH-410): a contenteditable with a tiny
// per-flavor toolbar (B/I/Link for body copy; Highlight for headings).
// The editable mirrors its HTML into a hidden input carrying the
// data-setting-path, so the page-editor auto-save sees a plain string.
// The server sanitizes to the allowlist on write AND render — this
// controller is UX, not the security boundary.
export default class extends Controller {
  static targets = ["editor", "input"];

  connect() {
    this.editorTarget.innerHTML = this.inputTarget.value;
    this.onInput = () => this.sync();
    this.editorTarget.addEventListener("input", this.onInput);
    // Enter → <br>, never contenteditable's <div> soup (divs get
    // stripped-to-text server-side, which would eat line breaks).
    this.onKeydown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        document.execCommand("insertLineBreak");
      }
    };
    this.editorTarget.addEventListener("keydown", this.onKeydown);
    // Paste as plain text — formatting comes from the toolbar only.
    this.onPaste = (e) => {
      e.preventDefault();
      document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    };
    this.editorTarget.addEventListener("paste", this.onPaste);
  }

  disconnect() {
    this.editorTarget.removeEventListener("input", this.onInput);
    this.editorTarget.removeEventListener("keydown", this.onKeydown);
    this.editorTarget.removeEventListener("paste", this.onPaste);
  }

  // Toolbar actions ride mousedown (click would steal the selection).
  bold(event)   { event.preventDefault(); this.exec("bold"); }
  italic(event) { event.preventDefault(); this.exec("italic"); }

  link(event) {
    event.preventDefault();
    const url = prompt("Link to (https://…, #book, or #next for the next funnel step):");
    if (url) this.exec("createLink", url);
  }

  highlight(event) {
    event.preventDefault();
    this.editorTarget.focus();
    const sel = window.getSelection();
    if (!sel.rangeCount) return;

    const insideMark = (node) => {
      for (let n = node; n && n !== this.editorTarget; n = n.parentNode) {
        if (n.nodeName === "MARK") return n;
      }
      return null;
    };
    const existing = insideMark(sel.anchorNode) || insideMark(sel.focusNode);
    if (existing) {
      // Toggle off: unwrap the mark.
      const parent = existing.parentNode;
      while (existing.firstChild) parent.insertBefore(existing.firstChild, existing);
      parent.removeChild(existing);
    } else {
      if (sel.isCollapsed) return;
      const range = sel.getRangeAt(0);
      const mark = document.createElement("mark");
      try {
        range.surroundContents(mark);
      } catch {
        // Selection crosses element boundaries — fall back to text-only.
        mark.textContent = range.toString();
        range.deleteContents();
        range.insertNode(mark);
      }
    }
    this.sync();
  }

  exec(command, arg = null) {
    this.editorTarget.focus();
    document.execCommand(command, false, arg);
    this.sync();
  }

  sync() {
    this.inputTarget.value = this.editorTarget.innerHTML;
    // Bubble an input event so the page editor's auto-save wakes up.
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }
}
